/**
 * Client HTTP de baixo nível para a API SNCR (Anvisa).
 * Usa o `httpClient` compartilhado do src-next (padrão sepro/oauth).
 */

import {
  httpGet,
  httpPost,
  type HttpError,
  type HttpRequestConfig,
  normalizeHttpError,
} from "src-next/shared/api/httpClient";

import { SNCR_API_DEFAULT, SNCR_NOTIFICACAO_QTD } from "../constants";
import type {
  SncrNotificacaoRequest,
  SncrNotificacaoResponse,
  SncrRetencaoRequest,
  SncrTokenResponse,
} from "../types";

const DEFAULT_TIMEOUT_MS = 60_000;
const NUMERACAO_TIMEOUT_MS = 90_000;

export function getSncrApiUrl(): string {
  const raw =
    (import.meta.env.VITE_ENDPOINT_SNCR as string | undefined)?.trim() ||
    SNCR_API_DEFAULT;
  return raw.replace(/\/+$/, "");
}

function buildSncrRequestConfig(
  extra?: HttpRequestConfig,
  accessToken?: string,
): HttpRequestConfig {
  return {
    ...extra,
    skipAuthInterceptors: true,
    withCredentials: false,
    timeout: extra?.timeout ?? DEFAULT_TIMEOUT_MS,
    headers: {
      Accept: "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...extra?.headers,
    },
  };
}

function isHttpError(error: unknown): error is HttpError {
  return Boolean(
    error &&
    typeof error === "object" &&
    "message" in error &&
    !("isAxiosError" in error),
  );
}

function toSncrError(httpError: HttpError, fallback: string): Error {
  const rawMessage = (httpError.message || "").toLowerCase();
  if (
    rawMessage.includes("timeout") ||
    rawMessage.includes("exceeded") ||
    httpError.status === 408
  ) {
    return new Error(
      "A API SNCR demorou demais para responder. Reconecte ao Gov.br e tente novamente.",
    );
  }

  const data = httpError.data as
    | { error?: string; mensagem?: string; message?: string }
    | string
    | undefined;

  if (typeof data === "string" && data.trim()) {
    return new Error(data);
  }

  if (data && typeof data === "object") {
    const message = data.error || data.mensagem || data.message;
    if (message) return new Error(message);
  }

  return new Error(
    httpError.message ||
    (httpError.status ? `${fallback}: ${httpError.status}` : fallback),
  );
}


export async function fetchSncrAccessToken(
  sessionId: string,
  requestConfig?: HttpRequestConfig,
): Promise<SncrTokenResponse> {
  const url = `${getSncrApiUrl()}/auth/token`;

  try {
    return await httpGet<SncrTokenResponse>(url, {
      ...buildSncrRequestConfig(requestConfig),
      params: { session_id: sessionId },
    });
  } catch (error) {
    const httpError = isHttpError(error) ? error : normalizeHttpError(error);
    console.error("[SNCR] Troca de token falhou", {
      status: httpError.status,
      data: httpError.data,
      message: httpError.message,
      origin: window.location.origin,
    });
    throw toSncrError(httpError, "SNCR token exchange failed");
  }
}

export async function sncrGet<T>(
  path: string,
  accessToken: string,
  requestConfig?: HttpRequestConfig,
): Promise<T> {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return httpGet<T>(
    `${getSncrApiUrl()}${normalizedPath}`,
    buildSncrRequestConfig(requestConfig, accessToken),
  );
}

export async function sncrPost<TResponse, TBody = unknown>(
  path: string,
  accessToken: string,
  body: TBody,
  requestConfig?: HttpRequestConfig,
): Promise<TResponse> {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return httpPost<TResponse, TBody>(
    `${getSncrApiUrl()}${normalizedPath}`,
    body,
    buildSncrRequestConfig(requestConfig, accessToken),
  );
}

function assertNotificacaoRequest(body: SncrNotificacaoRequest): void {
  const qtd = body.quantidade;
  if (
    !Number.isInteger(qtd) ||
    qtd < SNCR_NOTIFICACAO_QTD.min ||
    qtd > SNCR_NOTIFICACAO_QTD.max
  ) {
    throw new Error(
      `Quantidade deve ser entre ${SNCR_NOTIFICACAO_QTD.min} e ${SNCR_NOTIFICACAO_QTD.max}.`,
    );
  }
  if (!body.receita || !body.conselho || !body.uf || !body.documento) {
    throw new Error("Preencha tipo, conselho, UF e documento do prescritor.");
  }
}

export async function requestNotificacaoReceita(
  accessToken: string,
  body: SncrNotificacaoRequest,
  requestConfig?: HttpRequestConfig,
): Promise<SncrNotificacaoResponse> {
  assertNotificacaoRequest(body);

  try {
    return await sncrPost<SncrNotificacaoResponse, SncrNotificacaoRequest>(
      "/numeracoes/notificacao-receita",
      accessToken,
      {
        receita: body.receita,
        conselho: body.conselho,
        uf: body.uf.toUpperCase(),
        documento: body.documento.replace(/\D/g, ""),
        quantidade: body.quantidade,
      },
      { ...requestConfig, timeout: requestConfig?.timeout ?? NUMERACAO_TIMEOUT_MS },
    );
  } catch (error) {
    const httpError = isHttpError(error) ? error : normalizeHttpError(error);
    console.error("[SNCR] Solicitação de numeração falhou", {
      status: httpError.status,
      data: httpError.data,
      message: httpError.message,
    });
    throw toSncrError(httpError, "Falha ao solicitar numeração SNCR");
  }
}

function assertRetencaoRequest(body: SncrRetencaoRequest): void {
  if (!body.tipo || !body.conselho || !body.uf || !body.documento) {
    throw new Error("Preencha tipo, conselho, UF e documento do prescritor.");
  }
  const cnpj = body.cnpj.replace(/\D/g, "");
  if (cnpj.length !== 14) {
    throw new Error("CNPJ da organização inválido (14 dígitos).");
  }
}

export async function requestReceitaEspecialRetencao(
  accessToken: string,
  body: SncrRetencaoRequest,
  requestConfig?: HttpRequestConfig,
): Promise<SncrNotificacaoResponse> {
  assertRetencaoRequest(body);

  try {
    return await sncrPost<
      SncrNotificacaoResponse,
      {
        conselho: string;
        tipo: string;
        documento: string;
        uf: string;
        cnpj: string;
      }
    >(
      "/numeracoes/receita-especial-retencao",
      accessToken,
      {
        conselho: body.conselho,
        tipo: body.tipo,
        documento: body.documento.replace(/\D/g, ""),
        uf: body.uf.toUpperCase(),
        cnpj: body.cnpj.replace(/\D/g, ""),
      },
      { ...requestConfig, timeout: requestConfig?.timeout ?? NUMERACAO_TIMEOUT_MS },
    );
  } catch (error) {
    const httpError = isHttpError(error) ? error : normalizeHttpError(error);
    console.error("[SNCR] Solicitação de retenção falhou", {
      status: httpError.status,
      data: httpError.data,
      message: httpError.message,
    });
    throw toSncrError(
      httpError,
      "Falha ao solicitar numeração de retenção SNCR",
    );
  }
}
